import { AccountProductEnum } from '@varo/graphql-schema-types';
import { useRouter } from 'expo-router';
import {
  getSourceName,
  isPositiveValue,
} from 'features/accounts/AccountDetails/AccountDetails.helpers';
import useGetSmartViewTransactions from 'features/accounts/hooks/useGetSmartViewTransactions';
import NoTransactions from 'features/accounts/transactions/NoTransactions/NoTransactions';
import usePayOverTimeTransactionTaggedAnalytics from 'features/payOverTime/hooks/usePayOverTimeTransactionTaggedAnalytics';
import { type Ref, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import { propsFromTag } from 'shared/components/vxl/Tag/Tag.helpers';
import TransactionListItem from 'shared/components/vxl/TransactionListItem/TransactionListItem';
import BodySmBoldText from 'shared/components/vxl/typography/primary/body/BodySmBoldText';
import ErrorScreen from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';
import { isNotNil } from 'shared/utils/isNotNil/isNotNil';

export interface SmartViewRef {
  SmartViewTransactions: (e?: string) => void;
  pagingToken?: string | null;
}

export interface SmartViewTransactionsProps {
  accountId: string;
  pagingToken?: string;
  categoryId?: string | null | undefined;
  accountType?: AccountProductEnum;
  smartViewDataLoading?: boolean;
  selectedTab?: string;
  ref?: Ref<SmartViewRef>;
}

const SmartViewTransactions = ({
  accountId,
  categoryId,
  accountType,
  smartViewDataLoading,
  selectedTab,
  ref,
}: SmartViewTransactionsProps) => {
  const router = useRouter();
  const { t: translatedText } = useTranslation('accounts', {
    keyPrefix: 'transactions',
  });

  const {
    transactions,
    transactionError,
    loadingTransactions,
    SmartViewTransactions,
    loadingMoreData,
    isNewCategoryDataLoading,
    pagingToken,
  } = useGetSmartViewTransactions(accountId, categoryId);

  usePayOverTimeTransactionTaggedAnalytics(
    smartViewDataLoading ? undefined : transactions,
  );

  useImperativeHandle(ref, () => ({
    SmartViewTransactions,
    pagingToken,
  }));

  const handleOnPress = (transactionId: string) => {
    router.push({
      pathname: '/accounts/transaction-details',
      params: {
        transactionId,
        accountType,
      },
    });
  };

  if (!loadingTransactions && !transactionError && !transactions?.length) {
    return <NoTransactions title={translatedText('recentTransactions')} />;
  }

  return (
    <View style={styles.container}>
      {!smartViewDataLoading
        ? transactions?.map((listItem) => {
            return (
              <View key={listItem.id}>
                <BodySmBoldText style={styles.title} numberOfLines={0}>
                  {listItem.title.copy}
                </BodySmBoldText>
                {listItem?.children?.map((item, index) => {
                  const tags = item.tags.map(propsFromTag).filter(isNotNil);

                  return (
                    <View key={item.id}>
                      {index > 0 ? (
                        <View style={styles.divider}>
                          <HairLineDivider />
                        </View>
                      ) : null}
                      <TransactionListItem
                        amountStyle={isPositiveValue(item.primaryDetail.copy)}
                        isPending={
                          listItem.title.copy === translatedText('pendingText')
                        }
                        amountText={item.primaryDetail.copy}
                        titleText={item.primaryText.copy}
                        balanceText={item?.secondaryDetail?.copy}
                        accessibilityLabel=""
                        banner={{
                          body:
                            item?.callout?.children
                              ?.map((c) => c.html)
                              .join('') ?? '',
                        }}
                        onPress={() =>
                          handleOnPress(item?.onClick?.transaction?.id ?? '')
                        }
                        secondaryText={item?.secondaryText?.copy}
                        analyticsId="transactionViewed"
                        customAttributes={{
                          category:
                            accountType === AccountProductEnum.ChargeCard
                              ? 'varo_believe'
                              : accountType?.toLowerCase(),
                          account_type: accountType?.toLowerCase(),
                          source_name: getSourceName(selectedTab as string),
                        }}
                        tags={tags}
                      />
                    </View>
                  );
                })}
                {loadingMoreData ? (
                  <View style={styles.containerCentered}>
                    <LoadingSpinner />
                  </View>
                ) : null}
              </View>
            );
          })
        : null}
      <ScreenTransitionOverlay
        isLoading={loadingTransactions || isNewCategoryDataLoading}
        LoadingComponent={<LoadingSpinner />}
        ErrorComponent={
          transactionError ? <ErrorScreen {...transactionError} /> : null
        }
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.color.background.surface,
    flexDirection: 'column',
    flexShrink: 1,
    gap: theme.spacing.s,
  },
  containerCentered: {
    alignItems: 'center',
  },
  divider: {
    paddingHorizontal: theme.spacing.m,
  },
  title: {
    backgroundColor: theme.color.background.primary,
    paddingHorizontal: theme.spacing.m,
    paddingTop: theme.spacing.m,
  },
});

export default SmartViewTransactions;
